/** @jsxImportSource @emotion/react */

'use client';

import { css } from '@emotion/react';
import { Box, Stack } from '@mui/material';
import type { ReactNode } from 'react';

const styles = {
  root: css`
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 24px 16px;
  `,
  content: css`
    flex: 1;
    display: flex;
    flex-direction: column;
  `,
};

export interface WizardContextDrawerProps {
  /** Content to display in the context drawer */
  children: ReactNode;
  /** Whether to show entry animation */
  animate?: boolean;
}

/**
 * WizardContextDrawer - A container for contextual information in wizard layouts
 *
 * This component is designed to work within the WizardLayout context drawer slot.
 * It provides a consistent container for things like:
 * - Forecasting information
 * - Help text
 * - Preview panels
 * - Contextual actions
 *
 * The positioning is handled by the parent WizardLayout grid -
 * this component just provides the inner styling.
 */
export default function WizardContextDrawer({
  children,
  animate = true,
}: WizardContextDrawerProps) {
  return (
    <Box
      css={styles.root}
      sx={
        animate
          ? {
              animation: 'slideIn 0.3s ease-out',
              '@keyframes slideIn': {
                from: {
                  transform: 'translateX(20px)',
                  opacity: 0,
                },
                to: {
                  transform: 'translateX(0)',
                  opacity: 1,
                },
              },
            }
          : undefined
      }
    >
      <Stack
        css={styles.content}
        spacing={2}
      >
        {children}
      </Stack>
    </Box>
  );
}
